import React, { useState } from 'react';
import { Tabs, Tab } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';
import { useMutation } from '@apollo/client';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import { IPolicyPropertyDescriptionDetails } from '../../../..';
import CoverSplit from './CoverSplit';
import EnhancedButton from '@components/button/EnhancedButton';
import TabPanel from '@components/layout/tabs/TabPanel';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import { modifyPolicyDescription } from './queries';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { MAIN_THEME } from '@styles';

interface IDescriptionOfRiskPopUpFormProps {
  currencySymbol: string;
  data: IPolicyPropertyDescriptionDetails[];
  lovs: Record<string, Record<string, string>>;
  onClose: () => void;
  onSuccess: () => void;
}

const useStyles = makeStyles()(() => ({
  content: {
    padding: '0 63px 40px 30px',
  },
  tabsRoot: {
    minHeight: 'auto',
    borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
    marginBottom: '20px',
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: 'auto',
    padding: '6px 12px',
    marginRight: '4px',
    color: MAIN_THEME.color.black47,
    fontFamily: MAIN_THEME.font.regular,
    '&.Mui-selected': {
      color: MAIN_THEME.color.black,
      fontFamily: MAIN_THEME.font.medium,
      backgroundColor: MAIN_THEME.color.white98,
    },
  },
  firstGrid: {
    display: 'grid',
    gridTemplateColumns: '2fr 2fr 3fr',
    gridColumnGap: '21px',
    marginBottom: '20px',
  },
  singleItem: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  buttonsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '15px',
    marginTop: '3em',
  },
}));

const DescriptionOfRiskPopUpForm: React.FC<
  IDescriptionOfRiskPopUpFormProps
> = ({ currencySymbol, data, lovs, onClose, onSuccess }) => {
  const { classes } = useStyles();
  const params = useParams();

  const [modifyPolicyDescriptionAction] = useMutation(
    modifyPolicyDescription()
  );

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [propertyDescriptions, setPropertyDescriptions] =
    useState<IPolicyPropertyDescriptionDetails[]>(data);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleFieldUpdate = (
    index: number,
    fieldName: keyof IPolicyPropertyDescriptionDetails,
    value: string
  ) => {
    const newProps = [...propertyDescriptions];
    (newProps[index] as any)[fieldName] = value;
    setPropertyDescriptions(newProps);
  };

  const handleSubmit = async () => {
    const policyPropertyInput = propertyDescriptions.flatMap((desc, idx) =>
      desc.planCovers.map((cover) => ({
        RiskDescription: desc.riskDescription || '',
        Location: desc.location || '',
        GeographicalLocation: desc.geoLocationCode || '',
        PlanCoverID: cover.planCoverId || '',
        FireRate: cover.riskRate ?? 0,
        SumInsured: cover.sumInsured ?? 0,
        Premium: cover.premium ?? 0,
        Order: idx + 1,
      }))
    );

    try {
      await modifyPolicyDescriptionAction({
        variables: {
          entityId: params.id,
          policyPropertyInput,
        },
      });
      toast.success(
        <ToastSuccessMessage>
          Property Risk Description updated successfully!
        </ToastSuccessMessage>
      );
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
    }
  };

  return (
    <div className={classes.content}>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="Property Description Tabs"
        classes={{ root: classes.tabsRoot }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        {propertyDescriptions.map((_, index) => (
          <Tab
            key={`Risk-${index}`}
            label={`Risk ${index + 1}`}
            classes={{ root: classes.tabRoot }}
          />
        ))}
      </Tabs>

      <TabPanel key={`property-${selectedTab}`} index={selectedTab}>
        <div className={clsx('container', classes.firstGrid)}>
          <TextInputFormField
            name={`riskDescription-${selectedTab}`}
            title="Risk Description*"
            placeholder="Risk Description"
            value={propertyDescriptions[selectedTab].riskDescription || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'riskDescription', e.target.value)
            }
          />
          <SelectFormField
            name={`geoLocationCode-${selectedTab}`}
            title="Geographical Location*"
            placeholder="Geographical Location"
            value={propertyDescriptions[selectedTab].geoLocationCode || ''}
            onChange={(val: string) =>
              handleFieldUpdate(selectedTab, 'geoLocationCode', val)
            }
            selectOptions={lovs.geoLocations}
          />
          <TextInputFormField
            name={`location-${selectedTab}`}
            title="Location"
            placeholder="Location"
            value={propertyDescriptions[selectedTab].location || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'location', e.target.value)
            }
          />
        </div>
        <CoverSplit
          currencySymbol={currencySymbol}
          rows={propertyDescriptions[selectedTab].planCovers}
          lovs={lovs}
          onUpdateRows={(newRows) => {
            const updated = [...propertyDescriptions];
            updated[selectedTab].planCovers = newRows;
            setPropertyDescriptions(updated);
          }}
        />
      </TabPanel>

      <div className={classes.buttonsContainer}>
        <EnhancedButton onClick={onClose}>Cancel</EnhancedButton>
        <EnhancedButton isPrimary onClick={handleSubmit}>
          Submit
        </EnhancedButton>
      </div>
    </div>
  );
};

export default DescriptionOfRiskPopUpForm;
